import { useEffect, useRef } from 'react';

import { calculateAdditionalDiscountCrossValue } from '../utils/calculation';

function computeTotal(
  items: any[],
  discountOn: string | null | undefined,
  netAmountField = 'net_amount',
  grossAmountField = 'gross_amount'
): number {
  const field = discountOn === 'Gross Amount' ? grossAmountField : netAmountField;
  return items.reduce((sum: number, item: any) => sum + Number(item[field] || 0), 0);
}

export interface AdditionalDiscountAutoCalcConfig {
  items: any[];
  discountOn: string | null | undefined;
  percentage: number | string;
  amount: number | string;
  setValue: (name: string, value: any) => void;
  fieldConfig: {
    percentageField: string;
    amountField: string;
  };
  netAmountField?: string;
  grossAmountField?: string;

  /**
   * When true, auto-disabled logic is bypassed.
   * Caller explicitly controls field disabled states
   * via disablePercentageField / disableAmountField.
   * Auto sync (value calculation) still works regardless.
   */
  manualDisabled?: boolean;
  disablePercentageField?: boolean;
  disableAmountField?: boolean;
}

export function useAdditionalDiscountAutoCalc(config: AdditionalDiscountAutoCalcConfig) {
  const lastChangedBy = useRef<'user' | 'auto'>('user');
  const lastUserEdited = useRef<'percentage' | 'amount' | null>(null);

  // Percentage → Amount
  useEffect(() => {
    if (lastChangedBy.current === 'auto') {
      lastChangedBy.current = 'user';
      return;
    }
    if (!config.discountOn) return;
    const pct = Number(config.percentage);
    const amt = Number(config.amount);
    const total = computeTotal(config.items, config.discountOn, config.netAmountField, config.grossAmountField);

    if (!pct && amt && lastUserEdited.current === 'percentage') {
      config.setValue(config.fieldConfig.amountField, 0);
      lastUserEdited.current = null;
      return;
    }
    if (!pct || !total) return;

    lastUserEdited.current = 'percentage';
    lastChangedBy.current = 'auto';
    config.setValue(config.fieldConfig.amountField,
      calculateAdditionalDiscountCrossValue(pct, 'percentage', total));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.percentage, config.discountOn]);

  // Amount → Percentage
  useEffect(() => {
    if (lastChangedBy.current === 'auto') {
      lastChangedBy.current = 'user';
      return;
    }
    if (!config.discountOn) return;
    const pct = Number(config.percentage);
    const amt = Number(config.amount);
    const total = computeTotal(config.items, config.discountOn, config.netAmountField, config.grossAmountField);

    if (!amt && pct && lastUserEdited.current === 'amount') {
      config.setValue(config.fieldConfig.percentageField, 0);
      lastUserEdited.current = null;
      return;
    }
    if (!amt || !total) return;

    lastUserEdited.current = 'amount';
    lastChangedBy.current = 'auto';
    config.setValue(config.fieldConfig.percentageField,
      calculateAdditionalDiscountCrossValue(amt, 'amount', total));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.amount, config.discountOn]);

  const autoPercentageDisabled = !config.discountOn || (
    Boolean(Number(config.amount)) && lastUserEdited.current !== 'percentage'
  );
  const autoAmountDisabled = !config.discountOn || (
    Boolean(Number(config.percentage)) && lastUserEdited.current !== 'amount'
  );

  const isPercentageDisabled = config.manualDisabled
    ? (config.disablePercentageField ?? false)
    : autoPercentageDisabled;
  const isAmountDisabled = config.manualDisabled
    ? (config.disableAmountField ?? false)
    : autoAmountDisabled;

  const totalAmount = config.discountOn
    ? computeTotal(config.items, config.discountOn, config.netAmountField, config.grossAmountField)
    : 0;
  const isAmountExceeded = !!(config.discountOn && totalAmount > 0 && Number(config.amount) > totalAmount);
  const isPercentageExceeded = Number(config.percentage) > 100;

  return { isPercentageDisabled, isAmountDisabled, isAmountExceeded, isPercentageExceeded };
}
